import { useEffect, useRef, useState } from 'react';
import { handleInPageHashLinkClick, handleLandingLogoHomeClick } from '~/landing_page/lib/inPageNav';
import { cn } from '~/lib/utils';
import styles from './HeaderNav.module.css';

/** Exported so the footer renders the same nav and the two cannot drift apart. */
export const DEFAULT_HEADER_NAV_LINKS = [
  { label: 'Product', href: '/#features' },
  { label: "Who it's for", href: '/#use-cases' },
  { label: 'Pricing', href: '/#pricing' },
] as const;

export interface HeaderNavLink {
  label: string;
  href: string;
}

export interface HeaderNavProps {
  className?: string;
  navLinks?: readonly HeaderNavLink[];
  signUpHref?: string;
  /** `dark` is the landing v2 ground: same geometry, inverted ink. Defaults to the light landing. */
  tone?: 'light' | 'dark';
}

export function HeaderNav({
  className,
  navLinks = DEFAULT_HEADER_NAV_LINKS,
  signUpHref = '/sign-in',
  tone = 'light',
}: HeaderNavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    const SHOW_AT_TOP_PX = 8;
    const HIDE_AFTER_PX = 80;
    const DIRECTION_DELTA_PX = 4;

    const onScroll = () => {
      const y = window.scrollY;
      const last = lastScrollYRef.current;

      setScrolled(y > 1);

      if (y < SHOW_AT_TOP_PX) {
        setHidden(false);
      } else if (y > last + DIRECTION_DELTA_PX && y > HIDE_AFTER_PX) {
        setHidden(true);
      } else if (y < last - DIRECTION_DELTA_PX) {
        setHidden(false);
      }

      lastScrollYRef.current = y;
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        styles.nav,
        tone === 'dark' && styles.navDark,
        scrolled && styles.navScrolled,
        hidden && styles.navHidden,
        className,
      )}
      data-name="HeaderNav"
    >
      <div className={styles.navInner}>
        <div className={styles.navLeft}>
          <a
            className={styles.logoLink}
            href="/"
            aria-label="Deck home"
            onClick={handleLandingLogoHomeClick}
          >
            <img
              className={styles.logoMark}
              src="/landing/logo/deck-symbol.svg"
              alt=""
              width={24}
              height={22}
            />
          </a>

          <nav className={styles.navLinks} aria-label="Primary">
            {navLinks.map((item) => (
              <a
                key={item.label}
                className={styles.navLink}
                href={item.href}
                onClick={(e) => handleInPageHashLinkClick(e, item.href)}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>

        <div className={styles.navActions}>
          <a className={styles.signUpLink} href={signUpHref}>
            Sign up
          </a>
        </div>
      </div>
      <div className={styles.navHairline} aria-hidden />
    </header>
  );
}

HeaderNav.displayName = 'HeaderNav';
